import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  Alert,
  notification,
} from 'antd';
import {
  Lock,
  Unlock,
  LogOut,
  ShieldAlert,
  KeyRound,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import './SessionExpiredModal.scss';
import { useLanguage } from 'hooks/Language/useLanguage';
import { userLogin, userLoginStatus } from 'queries/ossix/auth/userLogin';
import { IUserLoginPayload } from 'interfaces/container/auth';
import { APP_TYPE } from 'constants/general';
import { notifySessionRestored } from 'helpers/apiRequest';

interface SessionExpiredModalProps {
  isVisible: boolean;
  onLogout: () => void;
  onSessionRestored: () => void;
}

const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({
  isVisible,
  onLogout,
  onSessionRestored,
}) => {
  const { translate } = useLanguage();
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [savedPassword, setSavedPassword] = useState<string>('');

  const userPID = localStorage.getItem('userPID') || '';

  const {
    loginStatusData,
    mutate: requestUserLoginStatus,
    loadLoginStatus,
  } = userLoginStatus();

  const {
    loginUserData,
    loadLoginUser,
    isLoginUserError,
    isLoginUserSuccess,
    loginUserMessage,
    mutate: requestUserLogin,
    clearData: clearLoginData,
  } = userLogin();

  const isOTPRequired =
    loginStatusData?.OTPRequired === 'YES' ||
    String(loginStatusData?.OTPRequired).toUpperCase() === 'YES';

  // Check login status on modal open
  useEffect(() => {
    if (isVisible && userPID) {
      requestUserLoginStatus({ PID: userPID });
    }
  }, [isVisible, userPID]);

  // Reset modal state when closed
  useEffect(() => {
    if (!isVisible) {
      form.resetFields();
      otpForm.resetFields();
      setErrorMessage('');
      setStep('password');
      setSavedPassword('');
      clearLoginData();
    }
  }, [isVisible, form, otpForm, clearLoginData]);

  // Handle Login Success / Error
  useEffect(() => {
    if (isLoginUserSuccess && loginUserData) {
      const rawData = loginUserData as unknown as Record<string, unknown> &
        Array<Record<string, unknown>>;
      const liveToken =
        (rawData?.LiveToken as string) ||
        (rawData?.[0]?.LiveToken as string) ||
        (Array.isArray(rawData) ? (rawData[0]?.LiveToken as string) : null);

      const refreshToken =
        (rawData?.RefreshToken as string) ||
        (rawData?.[0]?.RefreshToken as string) ||
        (Array.isArray(rawData) ? (rawData[0]?.RefreshToken as string) : null);

      if (liveToken) {
        localStorage.setItem('userToken', liveToken);
      }
      if (refreshToken) {
        localStorage.setItem('userRefreshToken', refreshToken);
      }
      localStorage.setItem('lastActiveTime', Date.now().toString());

      // Protect against stale in-flight 401 errors
      notifySessionRestored();

      // Notify GlobalLayout to reset its activity timer
      window.dispatchEvent(
        new CustomEvent('session-restored', {
          detail: { timestamp: Date.now() },
        }),
      );

      form.resetFields();
      otpForm.resetFields();
      clearLoginData();
      setErrorMessage('');
      setStep('password');
      setSavedPassword('');

      onSessionRestored();
      notification.success({
        message: translate('Session successfully restored!'),
        placement: 'topRight',
      });
    }

    if (isLoginUserError) {
      const msg = loginUserMessage || '';
      // If server explicitly complains about missing or required OTP, switch to OTP step smoothly
      if (
        msg.toLowerCase().includes('otp') ||
        msg.toLowerCase().includes('non fourni') ||
        msg.toLowerCase().includes('code')
      ) {
        setStep('otp');
        setErrorMessage(
          translate(
            'A security code (OTP) has been sent to your registered phone/email.',
          ),
        );
      } else {
        setErrorMessage(
          msg || translate('Incorrect password. Please try again.'),
        );
      }
    }
  }, [
    isLoginUserSuccess,
    isLoginUserError,
    loginUserData,
    loginUserMessage,
    form,
    otpForm,
    clearLoginData,
    onSessionRestored,
    translate,
  ]);

  const sendLoginRequest = (pwd: string, otpVal = '') => {
    const payload: IUserLoginPayload = {
      PID: userPID,
      Password: pwd,
      CountryCode: '',
      PhoneNumber: '',
      IMEI: '',
      SerialNumber: '',
      DeviceOS: 'Web',
      Description: 'AGB Web In-Place Re-authentication',
      OSVersion: '',
      MAC: '',
      Roaming: '0',
      DeviceType: 'Web',
      AppType: APP_TYPE,
      Longitude: 0,
      Latitude: 0,
      ClientName: 'AGB Web',
      ClientVersion: '1.0.0',
      PIN: '',
      OTP: otpVal,
      AppVersion: APP_TYPE.toString(),
      SelfieURL: '',
    };
    requestUserLogin(payload);
  };

  const handlePasswordSubmit = (values: { Password: string }) => {
    setErrorMessage('');
    setSavedPassword(values.Password);

    // If OTP is confirmed required for this account, transition directly to OTP step
    if (isOTPRequired) {
      setStep('otp');
      setErrorMessage('');
      return;
    }

    sendLoginRequest(values.Password, '');
  };

  const handleOtpSubmit = (values: { OTP: string }) => {
    setErrorMessage('');
    if (!savedPassword) {
      // If password was lost, ask for password again
      setStep('password');
      setErrorMessage(
        translate('Please enter your password to continue.'),
      );
      return;
    }
    sendLoginRequest(savedPassword, values.OTP);
  };

  const handleResendOTP = () => {
    if (userPID) {
      requestUserLoginStatus({ PID: userPID });
      notification.info({
        message: translate(
          'A security code (OTP) has been sent to your registered phone/email.',
        ),
        placement: 'topRight',
      });
    }
  };

  return (
    <Modal
      open={isVisible}
      footer={null}
      closable={false}
      centered
      width="min(440px, 95vw)"
      className="session-expired-modal"
      maskClosable={false}
      keyboard={false}
    >
      <div className="session-modal-content">
        {/* Original Amber Shield Icon */}
        <div className="session-icon-wrapper">
          <div className="session-icon-pulse" />
          <div className="session-icon-circle">
            {step === 'otp' ? (
              <KeyRound size={28} strokeWidth={2} />
            ) : (
              <ShieldAlert size={32} strokeWidth={2} />
            )}
          </div>
        </div>

        <h2 className="session-title">
          {step === 'otp'
            ? translate('Security code (OTP)')
            : translate('Session Expired')}
        </h2>

        <p className="session-description">
          {step === 'otp'
            ? translate(
                'Please enter the OTP code sent to your phone or email',
              )
            : translate(
                'Your session has expired for security reasons. Please log in again to continue using our services.',
              )}
        </p>

        {/* Error / Info Alert */}
        {errorMessage && (
          <Alert
            type={step === 'otp' && !isLoginUserError ? 'info' : 'error'}
            showIcon
            message={errorMessage}
            style={{ width: '100%', textAlign: 'left', borderRadius: 8 }}
          />
        )}

        {/* Step 1: Password Input */}
        {step === 'password' && (
          <Form
            form={form}
            onFinish={handlePasswordSubmit}
            layout="vertical"
            className="session-reauth-form"
            requiredMark={false}
          >
            <Form.Item
              name="Password"
              rules={[
                {
                  required: true,
                  message: translate('Enter your password'),
                },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input.Password
                prefix={
                  <Lock
                    size={17}
                    style={{ color: '#94A3B8', marginRight: 6 }}
                  />
                }
                placeholder={translate('Enter your password')}
                size="large"
                autoFocus
                className="session-password-input"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loadLoginUser || loadLoginStatus}
              icon={<Unlock size={18} strokeWidth={2} />}
              className="session-login-button"
              block
            >
              {loadLoginUser || loadLoginStatus
                ? translate('Authenticating...')
                : translate('Unlock & Continue')}
            </Button>
          </Form>
        )}

        {/* Step 2: Masked OTP Verification Code Input */}
        {step === 'otp' && (
          <Form
            form={otpForm}
            onFinish={handleOtpSubmit}
            layout="vertical"
            className="session-reauth-form"
            requiredMark={false}
          >
            <Form.Item
              name="OTP"
              rules={[
                {
                  required: true,
                  message: translate('Please enter the OTP code'),
                },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input.Password
                prefix={
                  <KeyRound
                    size={17}
                    style={{ color: '#94A3B8', marginRight: 6 }}
                  />
                }
                placeholder="••••••"
                size="large"
                autoFocus
                maxLength={8}
                autoComplete="one-time-code"
                className="session-password-input"
                style={{
                  letterSpacing: '6px',
                  fontWeight: 700,
                  fontSize: '18px',
                  textAlign: 'center',
                }}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loadLoginUser}
              icon={<Unlock size={18} strokeWidth={2} />}
              className="session-login-button"
              block
            >
              {loadLoginUser
                ? translate('Authenticating...')
                : translate('Verify & Unlock')}
            </Button>

            <div className="session-otp-helpers" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 8 }}>
              <Button
                type="link"
                size="small"
                onClick={handleResendOTP}
                icon={<RefreshCw size={13} />}
              >
                {translate('Resend code')}
              </Button>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setStep('password');
                  setErrorMessage('');
                }}
                icon={<ArrowLeft size={13} />}
              >
                {translate('Back to password')}
              </Button>
            </div>
          </Form>
        )}

        {/* Alternate Action: Logout / Switch Account */}
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
          <Button
            type="link"
            onClick={onLogout}
            icon={<LogOut size={15} />}
          >
            {translate('Log in with another account')}
          </Button>
          <p className="session-footer">
            {translate('This helps keep your account secure')}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default SessionExpiredModal;
